/* eslint-disable no-nested-ternary */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Collapse,
  Row,
  Col,
  Alert,
} from 'reactstrap';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { useFetch, Button, FormAlerts, Field } from '@cordata/buffet';
import { chain, sortBy } from 'lodash';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import kebabcaseKeys from 'kebabcase-keys';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { CSVReader } from 'react-papaparse';

import {
  authorizedRbInstance as Api,
  adminInstance as Admin,
} from '~/utils';

const intialValues = {
  validFile: false,
  dataType: '',
  file: '',
};

const validationSchema = Yup.object().shape({
  dataType: Yup.object().shape().nullable().required(),
  organization: Yup.object().shape().nullable().required(),
  division: Yup.object().shape().nullable().required(),
});

/* eslint-disable-next-line */
const syntaxHighlight = (j = '') => {
  const json = j.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  /* eslint-disable-next-line */
  return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, (match) => {
    let cls = 'number';
    if (/^"/.test(match)) {
      if (/:$/.test(match)) {
        cls = 'key';
      } else {
        cls = 'string';
      }
    } else if (/true|false/.test(match)) {
      cls = 'boolean';
    } else if (/null/.test(match)) {
      cls = 'null';
    }

    return (
      <span className={cls}>{match}</span>
    );
  });
};

const Pre = styled('pre')`
  outline: 1px solid #ccc;
  padding: 5px;
  margin: 5px;

  .string { color: green; }
  .number { color: darkorange; }
  .boolean { color: blue; }
  .null { color: magenta; }
  .key { color: red; }
`;

const ValidationFieldset = ({
  issues, alertColor, title, icon,
}) => (
  <fieldset>
    <Alert color={alertColor}>
      <h5 className="alert-heading">
        <FontAwesomeIcon icon={icon} className="mr-2" />
        {title}
      </h5>

      <hr />

      <ul>
        {Object.keys(issues).map(i => (
          <li><strong>{i}</strong>
            <ul>
              {issues[i].map(j => (
                <li>{j}</li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </Alert>
  </fieldset>
);

ValidationFieldset.propTypes = {
  issues: PropTypes.arrayOf().isRequired,
  alertColor: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
};

const BatchImportFields = () => {
  const {
    values, setFieldValue, isValid, dirty,
  } = useFormikContext();
  const [status, dataTypes] = useFetch('admin/import/data-types');
  const [organizations, setOrganizations] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const toastId = useRef(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [validationWarnings, setValidationWarnings] = useState({});

  const handleOnError = (err, file, inputElem, reason) => {
    console.error(err, file, inputElem, reason);
  };

  const notifyFileValidationCheck = () => {
    toastId.current = toast('Validating file ...', { autoClose: false });
  };

  const notifyFileValidationError = () =>
    toast.update(toastId.current, { render: 'Validation failed!', type: toast.TYPE.ERROR, autoClose: 2500 });

  const notifyFileValidationSuccess = () =>
    toast.update(toastId.current, { render: 'Validation succeeded!', type: toast.TYPE.SUCCESS, autoClose: 2500 });

  useEffect(() => {
    Admin
      .get('/organization')
      .then(r => r.data.data)
      .then(r => sortBy(r, ['attributes.orgName']))
      .then(setOrganizations);
  }, []);

  useEffect(() => {
    setDivisions([]);
    setFieldValue('division', null);

    if (values.organization) {
      Admin
        .get('/division', {
          params: {
            'org-uid': values.organization.id,
          },
        })
        .then(r => r.data.data)
        .then(r => sortBy(r, ['attributes.divisionName']))
        .then(setDivisions);
    }
  }, [values.organization]);

  useEffect(() => {
    if (isValid && dirty && values.dataType) {
      const params = kebabcaseKeys({
        divisionUid: values.division.id,
      }, { deep: true });

      Api
        .get(`/admin/import/schema/${values.dataType.attributes.name}`, {
          params,
        })
        .then(r => r.data)
        .then((r) => {
          setFieldValue('schema', r);
        });
    }
  }, [isValid, values.dataType]);

  const validateFile = ({ file, dataType }) => {
    notifyFileValidationCheck();

    const firstRow = file[0].data;

    const params = kebabcaseKeys({
      divisionUid: values.division.id,
    });

    const parseIssues = issues =>
      chain(issues)
        .toPairs()
        .map(p => p[1])
        .flatten()
        .uniq()
        .reduce((a, m) => ({
          ...a,
          [m]: Object.keys(issues).filter(ee => issues[ee].includes(m)),
        }), {})
        .value();

    const parseResponse = (data) => {
      setValidationWarnings({});
      setValidationErrors({});

      const { warnings, errors, success } = data;

      if (warnings) {
        setValidationWarnings(parseIssues(warnings));
      }

      if (errors) {
        setValidationErrors(parseIssues(errors));
      }

      setFieldValue('validFile', success);

      if (success) {
        notifyFileValidationSuccess();
      } else {
        notifyFileValidationError();
      }
    };

    Api
      .post(`/admin/batch-imports/${dataType.attributes.name}/validate`, firstRow, { params })
      .then(({ data }) =>
        parseResponse(data))
      .catch(({ response: { data } }) =>
        parseResponse(data));
  };

  useEffect(() => {
    if (isValid && values.file) {
      validateFile(values);
    } else if (!dirty && !isValid && !values.file) {
      setFieldValue('validFile', false);

      toast.error('fields absent');
    }
  }, [isValid, values.file]);

  return (
    <div>
      <fieldset>
        <Row form>
          <Col>
            <Field
              name="organization"
              options={organizations}
              type="select"
              getOptionValue={o => o.id}
              getOptionLabel={o => o.attributes.orgName}
              required
            />
          </Col>

          <Col>
            <Field
              name="division"
              options={divisions}
              type="select"
              getOptionValue={o => o.id}
              getOptionLabel={o => o.attributes.divisionName}
              disabled={!values.organization}
              required
            />
          </Col>
        </Row>
      </fieldset>

      <fieldset>
        <Field
          name="dataType"
          type="select"
          options={dataTypes}
          loading={status === 'loading'}
          getOptionValue={o => o.attributes.name}
          getOptionLabel={o => o.attributes.label}
          required
        />
      </fieldset>

      <fieldset>
        <CSVReader
          onDrop={f => setFieldValue('file', f)}
          onError={handleOnError}
        >
          <span>Drop CSV file here or click to upload.</span>
        </CSVReader>
      </fieldset>

      {[{
        issues: validationErrors,
        alertColor: 'danger',
        title: 'Errors',
        icon: 'minus-circle',
      }, {
        issues: validationWarnings,
        alertColor: 'warning',
        title: 'Warnings',
        icon: 'exclamation-triangle',
      }].map(i =>
        Object.keys(i.issues).length > 0 && (
        <ValidationFieldset {...i} />
      ))}
    </div>
  );
};

const BatchImportForm = ({ id, handleSubmit, handleCancel }) => {
  const [showSchema, setShowSchema] = useState(false);
  const toggle = () => setShowSchema(!showSchema);

  return (
    <Modal isOpen={!!id} backdrop="static">
      <Formik
        name="BatchImportForm"
        enableReinitialize
        initialValues={intialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          isSubmitting,
          submitCount,
          isValid,
          errors,
        }) => (
          <Form noValidate>
            <ModalHeader>{showSchema ? 'Schema' : id === 'add' ? 'Add' : 'Edit'}</ModalHeader>

            <ModalBody className="p-0">
              { submitCount >= 1 && errors && <FormAlerts errors={errors} /> }
              <Collapse isOpen={!showSchema}>
                <BatchImportFields />
              </Collapse>

              <Collapse isOpen={showSchema}>
                <fieldset>
                  <Pre>{JSON.stringify(values.schema, null, 2)}</Pre>
                </fieldset>
              </Collapse>

            </ModalBody>

            <ModalFooter className="d-flex">
              <div className="mr-auto">
                <Button
                  type="button"
                  outline={!showSchema}
                  onClick={toggle}
                  disabled={!isValid}
                >
                  {!showSchema ? 'Show' : 'Hide'} Schema
                </Button>
              </div>

              <div className="ml-auto">
                <Button
                  outline
                  type="cancel"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    showSchema ||
                    !isValid ||
                    !values.file ||
                    !values.validFile
                  }
                >
                  Import
                </Button>
              </div>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

BatchImportForm.propTypes = {
  id: PropTypes.string.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
};

export default BatchImportForm;
